import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Activity } from 'lucide-react';
import { fetchFleetSummary, fetchFleetActivity } from './api';
import {
  type FleetSummaryResponse,
  type ActivityEvent,
  formatRows,
  timeAgo,
  formatEta,
} from './types';

const POLL_MS = 30_000;

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-1">{value}</div>
      {sub ? <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div> : null}
    </div>
  );
}

function eventIcon(status: string) {
  if (status === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
  if (status === 'errored') return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
}

export function MigrationTab() {
  const [summary, setSummary] = useState<FleetSummaryResponse | null>(null);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        fetchFleetSummary(),
        fetchFleetActivity({ days: 7, limit: 100 }),
      ]);
      setSummary(s);
      setEvents(a.events);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <div className="space-y-4">
      {error ? (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : null}

      {summary ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Overall complete"
              value={`${summary.overallPct}%`}
              sub={`since ${summary.campaignStart}`}
            />
            <StatCard
              label="Stores"
              value={`${summary.stores.done}/${summary.stores.total}`}
              sub={`${summary.stores.withProblems} with problems`}
            />
            <StatCard
              label="Day-cells verified"
              value={`${summary.doneDays}/${summary.expectedDays}`}
              sub={`${summary.problemDays} failed · ETA ${formatEta(summary.etaMs)}`}
            />
            <StatCard
              label="Throughput"
              value={`${formatRows(Math.round(summary.throughputRowsPerMin))}/min`}
              sub="rows, last 60m"
            />
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">
              Per-platform completeness
            </div>
            <div className="space-y-1.5">
              {summary.platforms.map((p) => (
                <div key={p.platform} className="flex items-center gap-2 text-xs">
                  <span className="w-28 shrink-0 text-gray-700 dark:text-gray-300">
                    {p.platform}
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div className="h-full bg-green-500" style={{ width: `${p.pct}%` }} />
                  </div>
                  <span className="w-10 text-right text-gray-500">{p.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-xs text-gray-400">Loading summary…</div>
      )}

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <Activity className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Live activity
          </span>
          <span className="ml-auto text-[10px] text-gray-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> auto-refresh
            30s
          </span>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
          {events.length === 0 ? (
            <div className="px-4 py-6 text-xs text-gray-400">No recent runs.</div>
          ) : (
            events.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-center gap-3 text-xs">
                {eventIcon(e.status)}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  store {e.storeId}
                </span>
                <span className="text-gray-500">{e.platform}</span>
                <span className="text-gray-400">{e.runType}</span>
                <span className="ml-auto text-gray-400">{formatRows(e.rowsIngested)} rows</span>
                <span className="text-gray-400 w-16 text-right">{timeAgo(e.completedAt)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
